import { POST } from '../configs/api';
import { api } from './constants';

/**
 * Attachment file type helpers and the presigned upload flow for large files.
 *
 * Images and documents are posted to /uploads/direct through the API as before.
 * Videos are too large for that path (the API caps bodies at 10MB), so they are
 * uploaded straight to S3 with a presigned POST whose policy enforces the size cap.
 */

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB - must match S3_MAX_FILE_SIZE
export const MAX_VIDEO_SIZE_BYTES = 200 * 1024 * 1024; // 200MB - must match S3_MAX_VIDEO_FILE_SIZE

export const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

/** Value for the `accept` attribute of attachment file inputs. */
export const ACCEPT_ATTACHMENTS =
  'image/*,video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

export const isVideoFile = (fileType) => !!fileType && VIDEO_MIME_TYPES.includes(fileType);

export const isImageFile = (fileType) => !!fileType && fileType.startsWith('image/');

/** Images and videos both render as visual previews rather than a file icon. */
export const isPreviewableMedia = (fileType) => isImageFile(fileType) || isVideoFile(fileType);

/** Any `video/*` type, including ones we do not accept - used for friendlier errors. */
export const looksLikeVideo = (fileType) => !!fileType && fileType.startsWith('video/');

export const getMaxSizeForFile = (fileType) =>
  (isVideoFile(fileType) ? MAX_VIDEO_SIZE_BYTES : MAX_FILE_SIZE_BYTES);

export const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

/**
 * Validate a File against the accepted types and its per-type size cap.
 * @param {File} file
 * @returns {string|null} Error message, or null when the file is acceptable
 */
export const validateAttachmentFile = (file) => {
  if (!file) return 'File is missing';

  if (looksLikeVideo(file.type) && !isVideoFile(file.type)) {
    return `${file.name}: this video format is not supported. Please use MP4, MOV, or WebM.`;
  }

  const maxSize = getMaxSizeForFile(file.type);
  if (file.size > maxSize) {
    return `${file.name} is ${formatBytes(file.size)}. Maximum allowed is ${formatBytes(maxSize)}.`;
  }

  if (file.size === 0) {
    return `${file.name} is empty.`;
  }

  return null;
};

/** True when the rejection (or result) was caused by an explicit user cancel. */
export const isUploadCancelledError = (errorOrResult) => {
  if (!errorOrResult) return false;
  if (errorOrResult.cancelled) return true;
  const message = errorOrResult.message || errorOrResult;
  return typeof message === 'string' && /upload cancelled/i.test(message);
};

/** POST the file to S3 with progress reporting. Resolves on 2xx, rejects otherwise. */
const postToS3 = (uploadUrl, fields, file, onProgress, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const err = new Error('Upload cancelled');
      err.cancelled = true;
      reject(err);
      return;
    }

    const formData = new FormData();
    // S3 requires every policy field to precede the file field.
    Object.entries(fields || {}).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl, true);

    const cleanup = () => {
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      xhr.abort();
    };

    signal?.addEventListener('abort', onAbort);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      // S3 replies with an XML error document; surface the useful part.
      const code = /<Code>(.*?)<\/Code>/.exec(xhr.responseText || '')?.[1];
      if (code === 'EntityTooLarge') {
        reject(new Error(`File is larger than the ${formatBytes(MAX_VIDEO_SIZE_BYTES)} limit`));
        return;
      }
      reject(new Error(code ? `Storage rejected the upload (${code})` : 'Storage rejected the upload'));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error('Network error while uploading. Check your connection and try again.'));
    };

    xhr.onabort = () => {
      cleanup();
      const err = new Error('Upload cancelled');
      err.cancelled = true;
      reject(err);
    };

    xhr.send(formData);
  });

/**
 * Upload a file straight to S3 using a presigned POST, then register the
 * attachment. Used for videos.
 *
 * @param {File} file - The file to upload
 * @param {Object} meta - { entityType, entityId, fileName, category, isPrivate }
 * @param {Function} [onProgress] - Called with an integer percentage
 * @param {Object} [options] - { signal?: AbortSignal } to cancel the S3 transfer
 * @returns {Promise<{ error?: boolean, cancelled?: boolean, message?: string, attachment?: Object }>}
 */
export const uploadViaPresignedPost = async (file, meta, onProgress, options = {}) => {
  const { signal } = options;
  const { entityType, entityId, fileName, category, isPrivate } = meta;
  const displayName = (fileName || file.name || '').trim() || file.name;

  if (signal?.aborted) {
    return { error: true, cancelled: true, message: 'Upload cancelled' };
  }

  const sharedFields = {
    entityType,
    entityId,
    fileName: displayName,
    fileType: file.type,
    category,
    is_private: !!isPrivate,
  };

  const requested = await POST(api.getUploadPresignedUrl, {}, { ...sharedFields, fileSize: file.size });
  if (signal?.aborted) {
    return { error: true, cancelled: true, message: 'Upload cancelled' };
  }
  if (requested?.error || (requested?.status !== 200 && requested?.status !== 201)) {
    return {
      error: true,
      message:
        requested?.data?.errors?.[0]?.msg ||
        requested?.message ||
        requested?.data?.message ||
        'Failed to prepare the upload',
    };
  }

  const { uploadUrl, fields, s3Key, uploadToken } = requested?.data?.data || {};
  if (!uploadUrl || !s3Key || !uploadToken) {
    return { error: true, message: 'Failed to prepare the upload' };
  }

  try {
    await postToS3(uploadUrl, fields, file, onProgress, signal);
  } catch (uploadError) {
    return {
      error: true,
      cancelled: !!uploadError.cancelled || isUploadCancelledError(uploadError),
      message: uploadError.message,
    };
  }

  if (signal?.aborted) {
    // Bytes may already be in S3; confirm is skipped so no attachment row is created.
    // Orphan objects are cleaned by bucket lifecycle / confirm-side cleanup.
    return { error: true, cancelled: true, message: 'Upload cancelled' };
  }

  // Binding metadata is inside the uploadToken; do not re-send entity/category
  // fields the server already authorized at mint time.
  const confirmed = await POST(api.confirmUpload, {}, { uploadToken });
  if (confirmed?.error || (confirmed?.status !== 200 && confirmed?.status !== 201)) {
    return {
      error: true,
      message:
        confirmed?.data?.errors?.[0]?.msg ||
        confirmed?.message ||
        confirmed?.data?.message ||
        'Upload finished but could not be saved',
    };
  }

  return { attachment: confirmed?.data?.data };
};
